/**
 * The HTTP client every service in this app imports.
 *
 * This is the shared pattern: `frontend-partner` and `frontend-admin` carry a copy of this
 * file that differs only in the storage keys and the `client` sent at sign-in. It is
 * copied rather than published as a package because the three apps are separate git
 * repositories with no workspace tooling, so a package costs more than the duplication
 * saves. If you change something here that is not app-specific, change it in all three
 * and say so in docs/frontend-changes.md.
 *
 * Deliberately mirrors MachiExpress/services/client.ts, which has been running against
 * this API already — same envelope handling, same single-flight refresh, same shape of
 * retry. Two things are different, and both are corrections rather than divergence; see
 * `readFailure` and `Pagination` below.
 */

/**
 * Fail at import rather than at the first request.
 *
 * A missing base URL turns every call into a request against the Vite dev server, which
 * answers with the app's own index.html and a 200. The client then fails while parsing
 * HTML as JSON, three layers away from the actual mistake. Throwing here puts the real
 * message in the Vite overlay before a single screen renders.
 */
const BASE_URL = import.meta.env.VITE_API_URL

if (!BASE_URL) {
  throw new Error(
    'VITE_API_URL is not set. Copy .env.example to .env and restart the dev server — ' +
      'Vite only reads env files at startup.',
  )
}

/**
 * Tokens live in localStorage, not a cookie.
 *
 * Not a preference: the apps and the API are different sites, and `netlify.app` is on the
 * Public Suffix List, so a shared cookie was never available. That decision is recorded in
 * docs/intent/backend-to-live.md. The cost is honest — localStorage is readable by any
 * script that gets onto the page, so an XSS here is a stolen session. The mitigation is
 * the short access-token life (one hour) and server-side revocation on
 * sign-out, not the storage.
 */
const TOKEN_KEY = 'machi.user.jwt'
const REFRESH_KEY = 'machi.user.refresh'

export const readToken = () => localStorage.getItem(TOKEN_KEY)
export const readRefreshToken = () => localStorage.getItem(REFRESH_KEY)

export function writeSession(accessToken, refreshToken) {
  localStorage.setItem(TOKEN_KEY, accessToken)
  localStorage.setItem(REFRESH_KEY, refreshToken)
}

export function clearSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

/**
 * The same shape the mock module threw, so every screen's `catch (failure)` and every
 * `error?.message` keeps working unchanged.
 */
export class ApiError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/** The refresh token is gone or refused. Nothing to retry — sign in again. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super('Your session has ended. Please sign in again.', 401)
    this.name = 'SessionExpiredError'
  }
}

/**
 * Lets AuthContext react to a session ending inside a call it did not make — a list
 * refreshing in the background, say. Without this the app keeps rendering a signed-in
 * shell around endpoints that all return 401.
 */
let onSessionExpired = null
export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler
}

function endSession() {
  clearSession()
  onSessionExpired?.()

  return new SessionExpiredError()
}

/**
 * Read the message off a failure.
 *
 * The API is not symmetrical, and this is the trap: a success is
 * `{ success: true, message, data }` but a failure is `{ success: false, error }` —
 * there is no `message` key on an error and no `data` key either. Reading `.message`
 * yields undefined and every toast in the app says so.
 *
 * `message` is kept as a fallback only because a proxy or a future endpoint might send
 * one; `error` is the field this API actually uses. See docs/phase-0-audit.md, finding 1.
 */
function readFailure(body, status) {
  const text =
    body?.error ??
    body?.message ??
    (status >= 500 ? 'Machi Express is not answering. Try again in a moment.' : 'Something went wrong.')

  return new ApiError(text, status)
}

/** A body that is not JSON at all — an HTML error page, a proxy, an empty 204. */
async function parseBody(response) {
  const text = await response.text()

  if (!text) return null

  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * One refresh at a time, ever.
 *
 * Refresh tokens rotate: each refresh invalidates the one it was called with. Two calls
 * that both hit a 401 and both refresh would present the same token twice, and the server
 * reads the second as a replay — reuse detection then ends every session the account has.
 * So a console that fires three list requests on one screen would sign itself out. Sharing
 * one in-flight promise is what prevents it.
 */
let refreshing = null

async function refreshSession() {
  const refreshToken = readRefreshToken()

  if (!refreshToken) throw endSession()

  const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })

  const body = await parseBody(response)

  if (!response.ok || !body?.success) throw endSession()

  // The response carries a NEW refresh token. Storing only the access token means the
  // next refresh presents a spent one, which reads as a replay.
  writeSession(body.data.accessToken, body.data.refreshToken)

  return body.data.accessToken
}

function withRefreshLock() {
  if (!refreshing) {
    refreshing = refreshSession().finally(() => {
      refreshing = null
    })
  }

  return refreshing
}

function send(path, { method = 'GET', body, idempotencyKey, quoteToken, signal }, token) {
  return fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      // The delivery fee the customer agreed to, signed by the server. A header rather
      // than a body field, so the order payload is byte-for-byte what it has always been.
      ...(quoteToken ? { 'X-Quote-Id': quoteToken } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })
}

/**
 * A key for a write that must not happen twice.
 *
 * No partner endpoint requires one today — only `POST /api/orders` and the rider's
 * accept, pickup and deliver do. It lives here because `frontend-user` copies this file
 * and its one write is the one that must never double-fire.
 */
export const idempotencyKey = () =>
  crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Returns the whole envelope, not just `data`.
 *
 * Screens read both halves — `result.message` is what the success toasts show, and
 * `result.pagination` drives the pagers. Two pagination shapes exist: most lists come
 * from Paging.run and carry `hasMore` and `nextCursor`, but the account and invitation
 * lists carry only `page`, `limit`, `total` and `pages`. Nothing is normalised here, so
 * a screen must not assume `hasMore` exists.
 */
export async function request(path, options = {}) {
  let token = readToken()
  let response = await send(path, options, token)

  // One retry, and only when we actually sent a token — a 401 without one is an
  // unauthenticated call, and refreshing would not change the answer.
  if (response.status === 401 && token) {
    token = await withRefreshLock()
    response = await send(path, options, token)

    // Refreshed, presented the new token, still refused. The account lost the role, was
    // disabled, or had its sessions revoked. Stop; a second refresh cannot help.
    if (response.status === 401) throw endSession()
  }

  const body = await parseBody(response)

  if (!response.ok || !body?.success) throw readFailure(body, response.status)

  return body
}

export const get = (path, options) => request(path, { ...options, method: 'GET' })
export const post = (path, body, options) => request(path, { ...options, method: 'POST', body })
export const patch = (path, body, options) => request(path, { ...options, method: 'PATCH', body })
export const del = (path, options) => request(path, { ...options, method: 'DELETE' })
